/**
 * LoginPage.tsx
 * Page de connexion modernisée avec Tailwind CSS
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../components/auth/AuthProvider';
import { useToast } from '../components/react/common/ToastProvider';
import { RegisterPage } from './RegisterPage';
import { Mail, Lock, Loader2 } from 'lucide-react';

export function LoginPage(): React.ReactElement {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showRegister, setShowRegister] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { signIn, signInWithGoogle, resetPassword, user } = useAuthContext();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // P2.7 : Rediriger vers /editor (ou la route demandée) si déjà connecté.
  // LoginPage est rendu hors ProtectedRoute donc la nav doit être explicite.
  useEffect(() => {
    if (user) {
      // A15 : restaurer la route initialement demandée (chemin + query),
      // mémorisée par ProtectedRoute dans location.state.from.
      const fromLoc = (location.state as { from?: { pathname?: string; search?: string } } | null)
        ?.from;
      const target = fromLoc?.pathname ? `${fromLoc.pathname}${fromLoc.search ?? ''}` : '/editor';
      navigate(target, { replace: true });
    }
  }, [user, navigate, location.state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await signIn(email, password);
      if (error) {
        addToast({
          type: 'error',
          message: error.message || 'Erreur lors de la connexion',
        });
      } else {
        addToast({
          type: 'success',
          message: 'Connexion réussie',
        });
      }
    } catch (_error) {
      addToast({
        type: 'error',
        message: 'Une erreur inattendue est survenue',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // M4 : réinitialisation du mot de passe (le service existait mais aucune UI).
  const handleForgotPassword = async () => {
    if (!email.trim()) {
      addToast({ type: 'info', message: "Entrez d'abord votre adresse e-mail." });
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await resetPassword(email.trim());
      if (error) {
        addToast({ type: 'error', message: error.message || 'Échec de la réinitialisation' });
      } else {
        addToast({
          type: 'success',
          message: 'E-mail de réinitialisation envoyé. Vérifiez votre boîte de réception.',
        });
      }
    } catch (_error) {
      addToast({ type: 'error', message: 'Une erreur inattendue est survenue' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      const { error } = await signInWithGoogle();
      if (error) {
        addToast({
          type: 'error',
          message: error.message || 'Erreur lors de la connexion Google',
        });
      }
    } catch (_error) {
      addToast({
        type: 'error',
        message: 'Une erreur inattendue est survenue',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (showRegister) {
    return <RegisterPage onBackToLogin={() => setShowRegister(false)} />;
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        {/* Brand */}
        <div className="auth-brand">
          <div className="auth-brand-mark" aria-hidden="true">
            SP
          </div>
          <div>
            <h1 className="auth-title">Bienvenue sur Snapbooth</h1>
            <p className="auth-subtitle">Connectez-vous pour accéder à vos templates</p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="auth-field">
            <label htmlFor="email" className="auth-field-label">
              Email
            </label>
            <div className="auth-input-group">
              <span className="auth-input-icon" aria-hidden="true">
                <Mail />
              </span>
              <input
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                disabled={isLoading}
                placeholder="votre@email.com"
                autoComplete="email"
              />
            </div>
          </div>

          <div className="auth-field">
            <label htmlFor="password" className="auth-field-label">
              Mot de passe
            </label>
            <div className="auth-input-group">
              <span className="auth-input-icon" aria-hidden="true">
                <Lock />
              </span>
              <input
                id="password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                disabled={isLoading}
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>
            <div style={{ textAlign: 'right', marginTop: 6 }}>
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={isLoading}
                className="auth-link-btn"
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: 'var(--color-primary, #7C3AED)',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Mot de passe oublié ?
              </button>
            </div>
          </div>

          <button type="submit" disabled={isLoading} className="auth-btn-primary">
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Connexion…</span>
              </>
            ) : (
              <span>Se connecter</span>
            )}
          </button>
        </form>

        {/* Divider */}
        <div className="auth-divider">
          <span>ou</span>
        </div>

        {/* Google */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={isLoading}
          className="auth-btn-secondary"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          <span>Continuer avec Google</span>
        </button>

        {/* Footer */}
        <p className="auth-footer">
          Pas encore de compte ?{' '}
          <button type="button" onClick={() => setShowRegister(true)}>
            S'inscrire
          </button>
        </p>
      </div>
    </div>
  );
}
